import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { spaces } from '@99designs/emu/settings';
import {
    IconFacebook2,
    IconInstagram1,
    IconLink3,
    IconLinkedin4,
    IconSuccess,
    IconTwitter1,
} from '@99designs/icons';
import { trackSocialShare } from '../../../../lib/events';

const List = styled.ul`
    right: 35px;
    position: fixed;
    padding: ${spaces['0.75bu']} 0;
    box-shadow: rgb(0 0 0 / 20%) 0px 14px 42px 0px;
    top: 70px;
    background: white;
    border-radius: ${spaces['0.25bu']};
    width: 250px;
    ${mediaQuery.between('small', 'medium')} {
        right: 15px;
        top: ${spaces['3bu']};
    }
`;

const PopOverModal = styled.div`
    background: #000000;
    opacity: 10%;
    position: absolute;
    width: 100%;
    height: 100vh;
    top: 0;
    left: 0;
`;
const ListItem = styled.li`
    list-style-type: none;
    width: 100%;
    cursor: pointer;
    padding: ${spaces['0.75bu']} ${spaces['1.5bu']};
    box-sizing: border-box;
    &:hover {
        background-color: rgba(243, 242, 240);
    }
    &:focus {
        background-color: rgba(243, 242, 240);
    }
    &:active {
        background-color: rgba(243, 242, 240);
    }
    svg {
        height: ${spaces['1.5bu']};
        width: ${spaces['1.5bu']};
    }
`;

// Hack to position the Icon slightly more aligned with text
const VisuallyAlignedLinkedInIcon = styled(IconLinkedin4)`
    transform: translate(0px, -4px);
`;

const SocialMediaLink = styled(Link)`
    text-decoration: none;
    border-bottom: none;
    text-align: center;
    outline: 0;
`;

interface Props {
    showSocialShareMenu(show: boolean): void;
}

const socialMediaShareUrl = '99designs.com/design-without-borders';

export default function Share({ showSocialShareMenu }: Props) {
    type CopyLinkBtnState = 'INITIAL' | 'COPIED';
    const [copyLinkBtnState, setCopyLinkBtnState] = useState<CopyLinkBtnState>('INITIAL');

    const wrapperRef = useRef<HTMLUListElement>(null);
    const trackEventOptions = {
        share_subject_type: '21DWB',
        location: '21DWBHeader',
    };

    useEffect(() => {
        // function to check click outside Share component
        const handleClickOutside = (event: Event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                showSocialShareMenu(false);
            }
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, [wrapperRef, showSocialShareMenu]);

    return (
        <>
            <PopOverModal data-testid="modal-full"> </PopOverModal>
            <List ref={wrapperRef}>
                <ListItem>
                    <SocialMediaLink
                        href={`https://www.facebook.com/sharer/sharer.php?u=${socialMediaShareUrl}`}
                        openInNewTab={true}
                        onClick={() => trackSocialShare('Facebook', trackEventOptions)}
                    >
                        <IconFacebook2 /> Share on Facebook
                    </SocialMediaLink>
                </ListItem>
                <ListItem>
                    <SocialMediaLink
                        href={`https://www.instagram.com/s/aGlnaGxpZ2h0OjE3OTIzMTU0NjQ4MDgxNTU4`}
                        openInNewTab={true}
                        onClick={() => trackSocialShare('Instagram', trackEventOptions)}
                    >
                        <IconInstagram1 /> Share on Instagram
                    </SocialMediaLink>
                </ListItem>
                <ListItem>
                    <SocialMediaLink
                        href={`https://twitter.com/intent/tweet?url=${socialMediaShareUrl}`}
                        openInNewTab={true}
                        onClick={() => trackSocialShare('Twitter', trackEventOptions)}
                    >
                        <IconTwitter1 /> Share on Twitter
                    </SocialMediaLink>
                </ListItem>
                <ListItem>
                    <SocialMediaLink
                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${socialMediaShareUrl}`}
                        openInNewTab={true}
                        onClick={() => trackSocialShare('LinkedIn', trackEventOptions)}
                    >
                        <VisuallyAlignedLinkedInIcon /> Share on LinkedIn
                    </SocialMediaLink>
                </ListItem>
                <ListItem
                    onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        setCopyLinkBtnState('COPIED');
                        trackSocialShare('LinkCopied', trackEventOptions);
                    }}
                >
                    {copyLinkBtnState === 'INITIAL' ? (
                        <IconLink3 />
                    ) : (
                        <IconSuccess color="#75AAB3" />
                    )}
                    {copyLinkBtnState === 'INITIAL' ? ' Copy Link' : ' Link Copied'}
                </ListItem>
            </List>
        </>
    );
}
